/* Collective forms: Register Your Warranty, Make a Warranty Claim, Sponsor My Rideout.
 * - Registration talks to the Collective service through the Shopify app proxy (/apps/collective/...).
 * - Claims and rideouts post directly to Zoho Desk web forms (no Zoho API, no credentials in the theme).
 *   The Zoho embed code is pasted into the section settings; this file reads the form address, hidden keys and field
 *   names from it, so nothing about Zoho is hard-coded here.
 * Nothing in this file reports success unless the destination confirmed it.
 */
(() => {
  'use strict';
  const RECEIVED_FLAG = 'collective-form=received';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const readJson = (root, sel) => { try { return JSON.parse($(sel, root)?.textContent || '{}'); } catch (e) { return {}; } };
  // Shopify's t filter HTML-escapes translations (couldn't -> couldn&#39;t); turn them back into plain text once.
  const decodeText = (v) => { if (typeof v !== 'string' || !v.includes('&')) return v; const t = document.createElement('textarea'); t.innerHTML = v; return t.value; };
  const readI18n = (root) => Object.fromEntries(Object.entries(readJson(root, '[data-i18n]')).map(([k, v]) => [k, decodeText(v)]));
  const fmt = (s, vars = {}) => String(s || '').replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m)); // single braces: Liquid's t filter leaves them alone
  const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`);
  const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const bytes = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
  const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
  const longDate = (iso, locale) => { try { return new Intl.DateTimeFormat(locale || ((document.documentElement.lang || 'en') === 'en' ? 'en-GB' : document.documentElement.lang), { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`)); } catch (e) { return iso; } };

  /* ------------------------------------------------------------------ service (app proxy) */
  const Service = {
    base: '/apps/collective',
    async call(path, { method = 'GET', body } = {}) {
      let res;
      try {
        res = await fetch(`${this.base}${path}`, { method, credentials: 'same-origin', headers: body ? { 'content-type': 'application/json', accept: 'application/json' } : { accept: 'application/json' }, body: body ? JSON.stringify(body) : undefined });
      } catch (e) { throw Object.assign(new Error('network'), { code: 'network' }); }
      const type = res.headers.get('content-type') || '';
      if (!type.includes('application/json')) throw Object.assign(new Error('unavailable'), { code: 'unavailable', status: res.status });
      const data = await res.json();
      if (!res.ok) throw Object.assign(new Error(data.message || 'error'), { code: data.error, status: res.status, fields: data.fields });
      return data;
    },
    async status() { try { return await this.call('/status'); } catch (e) { return { registration: false, uploads: false }; } },
    /** Uploads one file into private storage; resolves { key, staffUrl? }. */
    async upload(purpose, file, onProgress) {
      const t = await this.call('/upload-token', { method: 'POST', body: { purpose } });
      if (file.size > t.maxBytes) throw Object.assign(new Error('too_large'), { code: 'too_large' });
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', t.uploadUrl + encodeURIComponent(t.token));
        xhr.setRequestHeader('content-type', file.type || 'application/octet-stream');
        xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total); };
        xhr.onload = () => { let d = {}; try { d = JSON.parse(xhr.responseText); } catch (e) { /* ignore */ } if (xhr.status === 201) resolve(d); else reject(Object.assign(new Error(d.message || 'upload_failed'), { code: d.error || 'upload_failed' })); };
        xhr.onerror = () => reject(Object.assign(new Error('network'), { code: 'network' }));
        xhr.send(file);
      });
    },
  };

  /* ------------------------------------------------------------------ Zoho Desk web form bridge */
  const FIELD_NAMES = {
    first: ['first name'],
    last: ['contact name', 'last name'],
    email: ['email'],
    phone: ['phone'],
    subject: ['subject'],
    description: ['description'],
    classification: ['classification'],
    category: ['category'],
  };
  const Zoho = {
    /** Reads Zoho's generated embed code (kept inert in a <template>). Returns null when it is missing or incomplete. */
    parse(template) {
      if (!template) return null;
      const root = template.content || template;
      const form = root.querySelector('form');
      if (!form) return null;
      const action = form.getAttribute('action') || '';
      if (!/^https:\/\/desk\.zoho\.[a-z.]+\/support\/WebToCase/i.test(action)) return null;
      const hidden = {};
      const names = {};
      const files = [];
      let descMax = 0;
      for (const el of form.querySelectorAll('input, textarea, select')) {
        const name = el.getAttribute('name');
        if (!name) continue;
        const type = (el.getAttribute('type') || '').toLowerCase();
        if (type === 'file') { files.push(name); continue; }
        if (type === 'hidden') { if (name !== 'returnURL') hidden[name] = el.getAttribute('value') || ''; continue; }
        if (type === 'submit' || type === 'reset' || type === 'button') continue;
        const lower = name.toLowerCase();
        for (const [key, list] of Object.entries(FIELD_NAMES)) {
          if (!names[key] && list.includes(lower)) {
            names[key] = name;
            if (key === 'description') descMax = Number(el.getAttribute('maxlength')) || 0;
            if ((key === 'classification' || key === 'category') && el.tagName === 'SELECT') names[`${key}Options`] = [...el.options].map((o) => o.value);
          }
        }
      }
      if (!names.last || !names.email || !names.subject || !names.description) return null;
      return { action, hidden, names, files, descMax };
    },

    /** Posts to Zoho through a hidden iframe. Resolves 'ok' only when Zoho redirected back to our own domain. */
    submit(z, values, files = []) {
      return new Promise((resolve) => {
        const target = `cf-zoho-${Date.now()}`;
        const iframe = document.createElement('iframe');
        iframe.name = target; iframe.hidden = true; iframe.title = ''; iframe.setAttribute('aria-hidden', 'true');
        document.body.append(iframe);
        const form = document.createElement('form');
        Object.assign(form, { action: z.action, method: 'POST', enctype: 'multipart/form-data', target, acceptCharset: 'UTF-8' });
        form.hidden = true;
        const add = (name, value) => { if (!name) return; const i = document.createElement('input'); i.type = 'hidden'; i.name = name; i.value = value ?? ''; form.append(i); };
        for (const [n, v] of Object.entries(z.hidden)) add(n, v);
        add('returnURL', `${location.origin}/robots.txt?${RECEIVED_FLAG}`);
        add(z.names.first, values.first);
        add(z.names.last, values.last);
        add(z.names.email, values.email);
        add(z.names.phone, values.phone);
        add(z.names.subject, values.subject);
        const d = document.createElement('textarea'); d.name = z.names.description; d.value = values.description; form.append(d);
        if (values.classification && z.names.classification && (!z.names.classificationOptions || z.names.classificationOptions.includes(values.classification))) add(z.names.classification, values.classification);
        if (values.category && z.names.category && (!z.names.categoryOptions || z.names.categoryOptions.includes(values.category))) add(z.names.category, values.category);
        files.slice(0, z.files.length).forEach((file, i) => {
          const input = document.createElement('input'); input.type = 'file'; input.name = z.files[i];
          const dt = new DataTransfer(); dt.items.add(file); input.files = dt.files; form.append(input);
        });
        document.body.append(form);
        let settled = false;
        const done = (result) => { if (settled) return; settled = true; clearTimeout(timer); setTimeout(() => { form.remove(); iframe.remove(); }, 1000); resolve(result); };
        const timer = setTimeout(() => done('timeout'), 180000);
        iframe.addEventListener('load', () => {
          let href = null;
          try { href = iframe.contentWindow.location.href; } catch (e) { href = null; } // cross-origin: still on Zoho
          if (href === 'about:blank') return;
          done(href && href.includes(RECEIVED_FLAG) ? 'ok' : 'unconfirmed');
        });
        form.submit();
      });
    },
  };

  /* ------------------------------------------------------------------ images */
  /** Re-encodes large photos to JPEG (max edge 2400px) so more evidence fits Zoho's attachment limits. HEIC is kept as is. */
  async function compressImage(file, maxEdge = 2400, quality = 0.85) {
    if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size < 1.5 * 1048576 || !window.createImageBitmap) return file;
    try {
      const bmp = await createImageBitmap(file);
      const scale = Math.min(1, maxEdge / Math.max(bmp.width, bmp.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(bmp.width * scale); canvas.height = Math.round(bmp.height * scale);
      canvas.getContext('2d').drawImage(bmp, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', quality));
      if (!blob || blob.size >= file.size) return file;
      return new File([blob], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg', lastModified: Date.now() });
    } catch (e) { return file; }
  }

  /* ------------------------------------------------------------------ step flow */
  class Flow {
    constructor(root, i18n) {
      this.root = root; this.i18n = i18n;
      this.steps = $$('[data-step]', root);
      this.progress = $('[data-progress]', root);
      this.index = 0;
      root.addEventListener('click', (e) => {
        const next = e.target.closest('[data-next]'); const back = e.target.closest('[data-back]');
        if (next) { e.preventDefault(); this.next(); }
        if (back) { e.preventDefault(); this.go(this.prevIndex()); }
      });
      root.addEventListener('input', (e) => { const f = e.target.closest('.field, .cf-choice-group, .cf-check'); if (f) f.classList.remove('is-invalid'); const m = f && $('.cf-error', f); if (m) m.remove(); });
      this.render();
    }
    visible(i) { const s = this.steps[i]; return s && !s.hasAttribute('data-skip'); }
    prevIndex() { let i = this.index - 1; while (i > 0 && !this.visible(i)) i -= 1; return Math.max(0, i); }
    nextIndex() { let i = this.index + 1; while (i < this.steps.length - 1 && !this.visible(i)) i += 1; return i; }
    current() { return this.steps[this.index]; }
    validate(step = this.current()) {
      let firstBad = null;
      $$('.cf-error', step).forEach((n) => n.remove());
      $$('.is-invalid', step).forEach((n) => n.classList.remove('is-invalid'));
      const fields = $$('input, select, textarea', step).filter((el) => !el.disabled && el.type !== 'hidden' && !el.closest('[hidden]'));
      const radiosSeen = new Set();
      for (const el of fields) {
        if (el.type === 'radio') { if (radiosSeen.has(el.name)) continue; radiosSeen.add(el.name); }
        let msg = '';
        if (el.dataset.match) { const other = $(`#${el.dataset.match}`, this.root); if (other && other.value.trim().toUpperCase() !== el.value.trim().toUpperCase()) msg = el.dataset.matchMessage; }
        if (!msg && el.dataset.minDate && el.value && el.value < el.dataset.minDate) msg = el.dataset.minDateMessage;
        if (!msg && el.checkValidity && !el.checkValidity()) {
          if (el.validity.valueMissing) msg = el.type === 'checkbox' ? this.i18n.required_check : el.type === 'radio' ? this.i18n.required_choice : this.i18n.required;
          else if (el.validity.typeMismatch && el.type === 'email') msg = this.i18n.invalid_email;
          else if (el.validity.rangeOverflow) msg = el.dataset.maxMessage || this.i18n.invalid_date;
          else if (el.validity.rangeUnderflow) msg = el.dataset.minDateMessage || el.dataset.minMessage || this.i18n.invalid;
          else msg = el.dataset.patternMessage || this.i18n.invalid;
        }
        if (!msg && step.dataset.customCheck && this.custom) msg = this.custom(el) || '';
        if (msg) { this.flag(el, msg); firstBad ||= el; }
      }
      const extra = this.onValidate ? this.onValidate(step) : null;
      if (extra && !firstBad) firstBad = extra;
      if (firstBad) { firstBad.focus({ preventScroll: false }); return false; }
      return true;
    }
    flag(el, msg) {
      const wrap = el.closest('.field, .cf-choice-group, .cf-check') || el.parentElement;
      wrap.classList.add('is-invalid');
      const p = document.createElement('p'); p.className = 'cf-error'; p.id = `${el.id || el.name}-error`.replace(/[^\w-]/g, '_'); p.textContent = msg;
      wrap.append(p);
      el.setAttribute('aria-describedby', p.id); el.setAttribute('aria-invalid', 'true');
    }
    next() { if (!this.validate()) return; if (this.beforeNext && this.beforeNext(this.current()) === false) return; this.go(this.nextIndex()); }
    go(i) {
      this.index = i;
      this.render();
      const h = $('[data-step-title]', this.current()) || this.current();
      h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true });
      const top = this.root.getBoundingClientRect().top + window.scrollY - (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-offset')) || 80) - 16;
      if (window.scrollY > top) window.scrollTo({ top, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      if (this.onEnter) this.onEnter(this.current());
    }
    render() {
      this.steps.forEach((s, i) => { s.hidden = i !== this.index; });
      const final = this.current()?.hasAttribute('data-final');
      this.root.classList.toggle('is-final', !!final);
      if (this.progress) {
        const visible = this.steps.map((_, i) => i).filter((i) => this.visible(i) && !this.steps[i].hasAttribute('data-final'));
        // Before a branch is chosen, count one place for it so the total doesn't jump from 4 to 5.
        if (this.steps.some((s) => s.dataset.stepOnly) && !this.steps.some((s, i) => s.dataset.stepOnly && this.visible(i))) visible.splice(1, 0, -1);
        const pos = visible.indexOf(this.index);
        this.progress.hidden = pos < 0;
        if (pos >= 0) {
          this.progress.textContent = fmt(this.i18n.progress, { current: pos + 1, total: visible.length });
          this.root.style.setProperty('--cf-progress', `${((pos + 1) / visible.length) * 100}%`);
        }
      }
    }
  }

  /* ------------------------------------------------------------------ file pickers */
  class FilePicker {
    /** opts: { max, accept: RegExp, maxBytes, compress, i18n } */
    constructor(el, opts) {
      this.el = el; this.opts = opts; this.files = [];
      this.input = $('input[type=file]', el);
      this.list = $('[data-file-list]', el);
      this.note = $('[data-file-note]', el);
      this.input.addEventListener('change', () => this.add([...this.input.files]));
      el.addEventListener('dragover', (e) => { e.preventDefault(); el.classList.add('is-drag'); });
      el.addEventListener('dragleave', () => el.classList.remove('is-drag'));
      el.addEventListener('drop', (e) => { e.preventDefault(); el.classList.remove('is-drag'); this.add([...e.dataTransfer.files]); });
      this.list.addEventListener('click', (e) => { const b = e.target.closest('[data-remove]'); if (b) { this.files.splice(Number(b.dataset.remove), 1); this.render(); this.el.dispatchEvent(new Event('change', { bubbles: true })); } });
    }
    async add(list) {
      const { i18n } = this.opts; const problems = [];
      for (const f of list) {
        if (this.files.length >= this.opts.max) { problems.push(fmt(i18n.file_too_many, { max: this.opts.max })); break; }
        const name = f.name || '';
        if (!this.opts.accept.test(f.type || '') && !this.opts.acceptExt?.test(name)) { problems.push(fmt(i18n.file_bad_type, { name })); continue; }
        const file = this.opts.compress ? await compressImage(f) : f;
        if (this.opts.maxBytes && file.size > this.opts.maxBytes) { problems.push(fmt(i18n.file_too_big, { name, max: bytes(this.opts.maxBytes) })); continue; }
        this.files.push(file);
      }
      this.input.value = '';
      this.render();
      if (this.note) { this.note.textContent = problems.join(' '); this.note.hidden = !problems.length; }
      this.el.classList.remove('is-invalid'); $('.cf-error', this.el)?.remove();
      this.el.dispatchEvent(new Event('change', { bubbles: true }));
    }
    render() {
      this.list.innerHTML = this.files.map((f, i) => `<li class="cf-file"><span class="cf-file__name">${esc(f.name)}</span><span class="cf-file__size">${bytes(f.size)}</span><button type="button" class="cf-file__remove" data-remove="${i}" aria-label="${esc(fmt(this.opts.i18n.file_remove, { name: f.name }))}">×</button></li>`).join('');
      this.list.hidden = !this.files.length;
    }
  }

  /* ------------------------------------------------------------------ shared: submission states */
  function setBusy(button, busy, label) {
    if (!button) return;
    if (busy) { button.dataset.label = button.textContent; button.textContent = label; button.setAttribute('aria-disabled', 'true'); button.disabled = true; }
    else { if (button.dataset.label) button.textContent = button.dataset.label; button.removeAttribute('aria-disabled'); button.disabled = false; }
  }
  function showStatus(root, kind, html) {
    const box = $('[data-status]', root);
    if (!box) return;
    box.className = `cf-status cf-status--${kind}`; box.innerHTML = html; box.hidden = false;
    box.setAttribute('tabindex', '-1'); box.focus();
  }
  function value(root, name) {
    const els = $$(`[name="${name}"]`, root);
    if (!els.length) return '';
    if (els[0].type === 'radio') return els.find((e) => e.checked)?.value || '';
    if (els[0].type === 'checkbox') return els.length > 1 ? els.filter((e) => e.checked).map((e) => e.value) : els[0].checked;
    return (els[0].value || '').trim();
  }
  /** Same submission twice within a day is stopped in this browser (protects against double tickets). */
  const Dedupe = {
    key: (s) => `cf:sent:${s}`,
    async hash(text) { const b = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text)); return [...new Uint8Array(b)].slice(0, 12).map((x) => x.toString(16).padStart(2, '0')).join(''); },
    async seen(text) { try { const k = this.key(await this.hash(text)); const t = Number(localStorage.getItem(k) || 0); return Date.now() - t < 86400000; } catch (e) { return false; } },
    async mark(text) { try { localStorage.setItem(this.key(await this.hash(text)), String(Date.now())); } catch (e) { /* private mode */ } },
  };

  function splitName(full) { const parts = String(full || '').trim().split(/\s+/); if (parts.length < 2) return { first: '', last: parts[0] || '' }; return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] }; }
  function lines(pairs) { return pairs.filter(([, v]) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && !v.length)).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('\n'); }

  /* ================================================================== REGISTER YOUR WARRANTY */
  async function initRegister(root) {
    const i18n = readI18n(root);
    const cfg = readJson(root, '[data-config]');
    const unavailable = $('[data-unavailable]', root);
    const app = $('[data-app]', root);
    if (!cfg.signedIn) return; // signed-out markup is rendered by Liquid
    const status = await Service.status();
    if (!status.registration) { unavailable.hidden = false; app.hidden = true; root.classList.remove('is-loading'); return; }

    let me; let catalogue = [];
    try { [me, { items: catalogue }] = await Promise.all([Service.call('/me'), Service.call('/catalogue')]); } catch (e) { unavailable.hidden = false; app.hidden = true; root.classList.remove('is-loading'); return; }
    root.classList.remove('is-loading');
    app.hidden = false;

    const flow = new Flow(app, i18n);
    const state = { submissionId: uuid(), source: '', item: null, product: null, proof: null, receipts: null };

    // Step: source
    const bikesBox = $('[data-direct-bikes]', app);
    const noneBox = $('[data-direct-none]', app);
    const preselect = new URLSearchParams(location.search).get('line');
    bikesBox.innerHTML = me.unregistered.map((u, i) => `
      <label class="cf-card">
        <input type="radio" name="item" value="${i}" required${preselect && preselect === u.lineItemId.split('/').pop() ? ' checked' : ''}>
        <span class="cf-card__media">${u.image ? `<img src="${esc(u.image)}&width=240" alt="" width="120" height="120" loading="lazy">` : ''}</span>
        <span class="cf-card__text"><span class="cf-card__title">${esc(u.title)}</span><span class="cf-card__meta">${esc(fmt(i18n.order_line, { order: u.orderName, date: longDate(u.purchaseDate) }))}</span></span>
      </label>`).join('');
    noneBox.hidden = me.unregistered.length > 0;
    bikesBox.hidden = !me.unregistered.length;
    if (me.registrations.length) {
      const reg = $('[data-already]', app);
      reg.hidden = false;
      $('[data-already-count]', reg).textContent = fmt(me.registrations.length === 1 ? i18n.already_one : i18n.already_many, { count: me.registrations.length });
    }

    const setSource = (src) => {
      state.source = src;
      $$('[data-only]', app).forEach((el) => { el.hidden = el.dataset.only !== src; $$('input, select, textarea', el).forEach((f) => { f.disabled = el.hidden; }); });
      $$('[data-step-only]', app).forEach((s) => { if (s.dataset.stepOnly === src) s.removeAttribute('data-skip'); else s.setAttribute('data-skip', ''); });
      flow.render();
    };
    $$('input[name="source"]', app).forEach((r) => r.addEventListener('change', () => setSource(r.value)));
    if (preselect) { const r = $('input[name="source"][value="direct"]', app); r.checked = true; setSource('direct'); }

    // Model picker (dealer purchases): search across current and discontinued models
    const search = $('[data-model-search]', app);
    const results = $('[data-model-results]', app);
    const other = $('[data-model-other]', app);
    const renderModels = () => {
      const q = search.value.trim().toLowerCase();
      const list = catalogue.filter((p) => !q || p.title.toLowerCase().includes(q)).slice(0, 24);
      results.innerHTML = list.length ? list.map((p) => `
        <label class="cf-card cf-card--compact">
          <input type="radio" name="product" value="${esc(p.id)}" required${state.product === p.id ? ' checked' : ''}>
          <span class="cf-card__media">${p.image ? `<img src="${esc(p.image)}&width=200" alt="" width="100" height="100" loading="lazy">` : ''}</span>
          <span class="cf-card__text"><span class="cf-card__title">${esc(p.title)}</span>${p.status === 'discontinued' ? `<span class="cf-card__meta">${esc(i18n.discontinued)}</span>` : ''}</span>
        </label>`).join('') : `<p class="cf-hint">${esc(i18n.no_models)}</p>`;
    };
    search.addEventListener('input', renderModels);
    results.addEventListener('change', (e) => { if (e.target.name === 'product') state.product = e.target.value; });
    other.addEventListener('change', () => {
      const on = other.checked;
      $('[data-model-other-field]', app).hidden = !on;
      $('[data-model-other-field] input', app).disabled = !on;
      $$('input[name="product"]', results).forEach((r) => { r.required = !on; if (on) r.checked = false; });
      if (on) state.product = null;
      search.disabled = on;
    });
    renderModels();

    // Assembly done / pending
    const assemblyRadios = $$('input[name="assembly_status"]', app);
    const assemblyFields = $('[data-assembly-fields]', app);
    const pendingNote = $('[data-assembly-pending]', app);
    assemblyRadios.forEach((r) => r.addEventListener('change', () => {
      const done = r.value === 'done' && r.checked;
      if (!r.checked) return;
      assemblyFields.hidden = !done; pendingNote.hidden = done;
      $$('input, select, textarea', assemblyFields).forEach((f) => { f.disabled = !done; });
    }));

    const fileOpts = { max: 3, accept: /^(image\/(jpeg|png|webp|heic|heif)|application\/pdf)$/, acceptExt: /\.(heic|heif)$/i, maxBytes: 15 * 1048576, compress: true, i18n };
    const proofPicker = new FilePicker($('[data-files="proof"]', app), fileOpts);
    const receiptPicker = new FilePicker($('[data-files="assembly"]', app), fileOpts);

    flow.onValidate = (step) => {
      if (step.dataset.stepName === 'dealer' && !proofPicker.files.length) { flow.flag(proofPicker.input, i18n.proof_required); return proofPicker.input; }
      return null;
    };
    flow.beforeNext = (step) => {
      if (step.dataset.stepName === 'direct') {
        const i = Number(value(app, 'item'));
        state.item = me.unregistered[i];
        const date = $('#RegAssemblyDate', app);
        date.dataset.minDate = state.item.purchaseDate; date.min = state.item.purchaseDate;
      }
      if (step.dataset.stepName === 'dealer') {
        const pd = value(app, 'purchase_date');
        const date = $('#RegAssemblyDate', app);
        date.dataset.minDate = pd; date.min = pd;
      }
      return true;
    };
    flow.onEnter = (step) => { if (step.dataset.stepName === 'review') renderReview(); };

    const renderReview = () => {
      const done = value(app, 'assembly_status') === 'done';
      const model = state.source === 'direct' ? state.item?.title : (catalogue.find((p) => p.id === state.product)?.title || value(app, 'model_other'));
      const rows = [
        [i18n.r_bike, model],
        [i18n.r_bought, state.source === 'direct' ? fmt(i18n.order_line, { order: state.item?.orderName, date: longDate(state.item?.purchaseDate) }) : `${value(app, 'retailer_name')}, ${value(app, 'retailer_town')} · ${longDate(value(app, 'purchase_date'))}`],
        [i18n.r_frame, value(app, 'frame_number').toUpperCase()],
        [i18n.r_assembly, done ? `${value(app, 'assembly_shop')}, ${value(app, 'assembly_town')} · ${longDate(value(app, 'assembly_date'))}${value(app, 'assembly_mechanic') ? ` · ${value(app, 'assembly_mechanic')}` : ''}` : i18n.r_assembly_pending],
        [i18n.r_files, [...(state.source === 'dealer' ? proofPicker.files : []), ...(done ? receiptPicker.files : [])].map((f) => f.name).join(', ') || '—'],
      ];
      $('[data-review]', app).innerHTML = rows.map(([k, v]) => `<div class="cf-review__row"><dt>${esc(k)}</dt><dd>${esc(v || '—')}</dd></div>`).join('');
    };

    const submit = $('[data-submit]', app);
    submit.addEventListener('click', async (e) => {
      e.preventDefault();
      if (!flow.validate()) return;
      setBusy(submit, true, i18n.sending);
      $('[data-status]', app).hidden = true;
      try {
        const done = value(app, 'assembly_status') === 'done';
        const upload = async (picker, purpose) => { const keys = []; for (const [n, f] of picker.files.entries()) { submit.textContent = fmt(i18n.uploading, { current: n + 1, total: picker.files.length }); keys.push((await Service.upload(purpose, f)).key); } return keys; };
        state.proof ||= state.source === 'dealer' ? await upload(proofPicker, 'proof') : [];
        state.receipts ||= done ? await upload(receiptPicker, 'assembly') : [];
        submit.textContent = i18n.sending;
        const body = {
          submissionId: state.submissionId,
          source: state.source,
          frameNumber: value(app, 'frame_number').toUpperCase(),
          consentData: value(app, 'consent_data') === true,
          assembly: done ? { status: 'done', shopName: value(app, 'assembly_shop'), town: value(app, 'assembly_town'), country: value(app, 'assembly_country'), date: value(app, 'assembly_date'), mechanic: value(app, 'assembly_mechanic'), receiptKeys: state.receipts, confirmProfessional: value(app, 'confirm_professional') === true } : { status: 'pending' },
          website_url: value(app, 'website_url'),
        };
        if (state.source === 'direct') Object.assign(body, { orderId: state.item.orderId, lineItemId: state.item.lineItemId });
        else Object.assign(body, { productId: state.product ? state.product.split('/').pop() : undefined, modelOther: state.product ? undefined : value(app, 'model_other'), retailerName: value(app, 'retailer_name'), retailerTown: value(app, 'retailer_town'), retailerCountry: value(app, 'retailer_country'), purchaseDate: value(app, 'purchase_date'), proofKeys: state.proof });
        const result = await Service.call('/registrations', { method: 'POST', body });
        const doneStep = $('[data-final]', app);
        $('[data-ref]', doneStep).textContent = result.reference;
        $('[data-done-pending]', doneStep).hidden = result.status !== 'awaiting_assembly';
        $('[data-done-already]', doneStep).hidden = !result.alreadyRegistered;
        flow.go(flow.steps.indexOf(doneStep));
      } catch (err) {
        setBusy(submit, false);
        const msg = err.code === 'validation_failed' && err.fields ? `${i18n.err_fields} ${Object.keys(err.fields).join(', ')}` : err.code === 'network' ? i18n.err_network : err.code === 'unavailable' ? i18n.err_unavailable : (err.message || i18n.err_generic);
        showStatus(app, 'error', `<p>${esc(msg)}</p><p>${esc(i18n.err_help)}</p>`);
        return;
      }
      setBusy(submit, false);
    });
  }

  /* ================================================================== MY BIKES (account) */
  async function initMyBikes(root) {
    const i18n = readI18n(root);
    const list = $('[data-list]', root);
    const status = await Service.status();
    if (!status.registration) { $('[data-unavailable]', root).hidden = false; root.classList.remove('is-loading'); return; }
    let me;
    try { me = await Service.call('/me'); } catch (e) { $('[data-unavailable]', root).hidden = false; root.classList.remove('is-loading'); return; }
    root.classList.remove('is-loading');
    const withParam = (base, k, v) => { const u = new URL(base, location.origin); u.searchParams.set(k, v); return u.pathname + u.search; };
    const regUrl = root.dataset.registerUrl; const claimUrl = root.dataset.claimUrl;
    const cards = [
      ...me.registrations.map((r) => `
        <li class="cf-bike">
          <p class="cf-bike__status label">${esc(r.status === 'awaiting_assembly' ? i18n.status_pending : i18n.status_registered)}</p>
          <h3 class="cf-bike__title">${esc(r.model || '')}</h3>
          <dl class="cf-bike__facts">
            <div><dt>${esc(i18n.reference)}</dt><dd>${esc(r.reference)}</dd></div>
            <div><dt>${esc(i18n.frame)}</dt><dd>${esc(r.frameNumber)}</dd></div>
            <div><dt>${esc(i18n.purchased)}</dt><dd>${esc(r.orderName ? `${r.orderName} · ${longDate(r.purchaseDate)}` : `${r.retailer || ''} · ${longDate(r.purchaseDate)}`)}</dd></div>
            <div><dt>${esc(i18n.assembly)}</dt><dd>${esc(r.assembly ? `${r.assembly.shop} · ${longDate(r.assembly.date)}` : i18n.assembly_missing)}</dd></div>
          </dl>
          <div class="button-row">
            ${r.status === 'awaiting_assembly' ? `<a class="button" href="${esc(withParam(regUrl, 'complete', r.id.split('/').pop()))}">${esc(i18n.add_assembly)}</a>` : ''}
            ${r.source === 'direct' ? `<a class="button button--secondary" href="${esc(withParam(claimUrl, 'registration', r.reference))}">${esc(i18n.claim)}</a>` : ''}
          </div>
        </li>`),
      ...me.unregistered.map((u) => `
        <li class="cf-bike cf-bike--todo">
          <p class="cf-bike__status label">${esc(i18n.status_unregistered)}</p>
          <h3 class="cf-bike__title">${esc(u.title)}</h3>
          <p class="cf-hint">${esc(fmt(i18n.order_line, { order: u.orderName, date: longDate(u.purchaseDate) }))}</p>
          <a class="button" href="${esc(withParam(regUrl, 'line', u.lineItemId.split('/').pop()))}">${esc(i18n.register)}</a>
        </li>`),
    ];
    list.innerHTML = cards.join('');
    $('[data-empty]', root).hidden = cards.length > 0;
  }

  /* ---- complete a pending registration (assembly details added later) */
  async function initComplete(root, id) {
    const i18n = readI18n(root);
    const box = $('[data-complete]', root);
    const app = $('[data-app]', root);
    const status = await Service.status();
    if (!status.registration) return false;
    app.hidden = true; box.hidden = false; root.classList.remove('is-loading');
    const form = $('form', box);
    const picker = new FilePicker($('[data-files="assembly-later"]', box), { max: 3, accept: /^(image\/(jpeg|png|webp|heic|heif)|application\/pdf)$/, acceptExt: /\.(heic|heif)$/i, maxBytes: 15 * 1048576, compress: true, i18n });
    const flow = new Flow(box, i18n);
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!flow.validate(box)) return;
      const btn = $('button[type=submit]', form);
      setBusy(btn, true, i18n.sending);
      try {
        const keys = []; for (const f of picker.files) keys.push((await Service.upload('assembly', f)).key);
        await Service.call(`/registrations/${encodeURIComponent(id)}`, { method: 'POST', body: { assembly: { status: 'done', shopName: value(form, 'c_shop'), town: value(form, 'c_town'), country: value(form, 'c_country'), date: value(form, 'c_date'), mechanic: value(form, 'c_mechanic'), receiptKeys: keys, confirmProfessional: value(form, 'c_confirm') === true } } });
        form.hidden = true;
        showStatus(box, 'ok', `<p class="cf-status__title">${esc(i18n.complete_done)}</p>`);
      } catch (err) {
        setBusy(btn, false);
        showStatus(box, 'error', `<p>${esc(err.message || i18n.err_generic)}</p><p>${esc(i18n.err_help)}</p>`);
      }
    });
    return true;
  }

  /* ================================================================== WARRANTY CLAIM */
  function initClaim(root) {
    const i18n = readI18n(root);
    const cfg = readJson(root, '[data-config]');
    const zoho = Zoho.parse($('template[data-zoho]', root));
    const app = $('[data-app]', root);
    if (!zoho) { $('[data-unavailable]', root).hidden = false; app.hidden = true; return; }
    const flow = new Flow(app, i18n);

    // Source gate: dealer purchases go back to the retailer
    $$('input[name="claim_source"]', app).forEach((r) => r.addEventListener('change', () => {
      const dealer = r.value === 'dealer' && r.checked;
      $('[data-dealer-stop]', app).hidden = !dealer;
      $('[data-source-next]', app).hidden = dealer;
    }));

    // Signed-in customers pick a bike from their orders (rendered by Liquid) or type an order number
    const pick = $$('input[name="claim_item"]', app);
    const manual = $('[data-manual-order]', app);
    pick.forEach((r) => r.addEventListener('change', () => {
      const isManual = r.value === 'manual' && r.checked;
      if (!r.checked) return;
      manual.hidden = !isManual; $$('input', manual).forEach((f) => { f.disabled = !isManual; });
    }));

    const photos = new FilePicker($('[data-files="photos"]', app), { max: Math.max(1, zoho.files.length - 1), accept: /^image\/(jpeg|png|webp|heic|heif)$/, acceptExt: /\.(heic|heif)$/i, maxBytes: cfg.zohoMaxBytes, compress: true, i18n });
    const videoEl = $('[data-files="video"]', app);
    const video = new FilePicker(videoEl, { max: 1, accept: /^video\//, maxBytes: cfg.videoMaxBytes, compress: false, i18n });
    let uploads = false;
    Service.status().then((s) => { uploads = !!s.uploads; $('[data-video-large]', app).hidden = !uploads; $('[data-video-small]', app).hidden = uploads; });

    flow.onValidate = (step) => {
      if (step.dataset.stepName === 'evidence' && !photos.files.length) { flow.flag(photos.input, i18n.photos_required); return photos.input; }
      if (step.dataset.stepName === 'evidence' && video.files[0] && video.files[0].size > cfg.zohoMaxBytes && !uploads) { flow.flag(video.input, fmt(i18n.video_too_big_no_service, { max: bytes(cfg.zohoMaxBytes) })); return video.input; }
      return null;
    };
    flow.onEnter = (step) => { if (step.dataset.stepName === 'review') $('[data-review]', app).innerHTML = review().map(([k, v]) => `<div class="cf-review__row"><dt>${esc(k)}</dt><dd>${esc(v || '—')}</dd></div>`).join(''); };

    const bike = () => {
      const r = pick.find((x) => x.checked);
      if (r && r.value !== 'manual') return { title: r.dataset.title, order: r.dataset.order, date: r.dataset.date, registration: new URLSearchParams(location.search).get('registration') || '' };
      return { title: value(app, 'bike_model'), order: value(app, 'order_number'), date: '', registration: new URLSearchParams(location.search).get('registration') || '' };
    };
    const review = () => {
      const b = bike();
      return [
        [i18n.r_bike, b.title], [i18n.r_order, b.order], [i18n.r_frame, value(app, 'frame_number').toUpperCase()],
        [i18n.r_part, value(app, 'part')], [i18n.r_noticed, value(app, 'noticed_on') ? longDate(value(app, 'noticed_on')) : ''],
        [i18n.r_files, [...photos.files, ...video.files].map((f) => f.name).join(', ')],
        [i18n.r_contact, `${value(app, 'name')} · ${value(app, 'email')}${value(app, 'phone') ? ` · ${value(app, 'phone')}` : ''}`],
      ];
    };

    const submit = $('[data-submit]', app);
    submit.addEventListener('click', async (e) => {
      e.preventDefault();
      if (!flow.validate()) return;
      if (value(app, 'website_url')) return; // honeypot
      const b = bike();
      const name = splitName(value(app, 'name'));
      let videoLine = '';
      const attach = [...photos.files];
      setBusy(submit, true, i18n.sending);
      try {
        const v = video.files[0];
        if (v && v.size <= cfg.zohoMaxBytes && attach.length < zoho.files.length) attach.push(v);
        else if (v) {
          submit.textContent = fmt(i18n.uploading_video, { pct: 0 });
          const r = await Service.upload('claim-video', v, (p) => { submit.textContent = fmt(i18n.uploading_video, { pct: Math.round(p * 100) }); });
          videoLine = r.staffUrl;
        }
      } catch (err) {
        setBusy(submit, false);
        showStatus(app, 'error', `<p>${esc(i18n.video_failed)}</p>`);
        return;
      }
      const subject = [cfg.subjectPrefix, b.title, b.order].filter(Boolean).join(' | ');
      const description = [
        `${cfg.subjectPrefix.toUpperCase()}`,
        lines([
          ['Purchased from', 'collectivebikes.com'], ['Bike', b.title], ['Order', b.order], ['Order date', b.date], ['Warranty registration', b.registration],
          ['Frame number (as entered by customer)', value(app, 'frame_number').toUpperCase()],
          ['Part affected', value(app, 'part')], ['Noticed on', value(app, 'noticed_on')],
          ['Assembled by', value(app, 'assembled_by')],
          ['Signed in', cfg.customerId ? `yes (Shopify customer ${cfg.customerId})` : 'no'],
          ['Photos attached', String(attach.filter((f) => f.type.startsWith('image/')).length)],
          ['Video', videoLine ? `private link (staff only): ${videoLine}` : (video.files[0] ? 'attached' : 'none')],
        ]),
        '',
        `WHAT IS WRONG\n${value(app, 'issue')}`,
        '',
        `HOW IT HAPPENED\n${value(app, 'how')}`,
      ].join('\n');
      if (zoho.descMax && description.length > zoho.descMax) { setBusy(submit, false); showStatus(app, 'error', `<p>${esc(fmt(i18n.too_long, { over: description.length - zoho.descMax }))}</p>`); return; }
      const fingerprint = `${value(app, 'email')}|${subject}|${value(app, 'issue')}`;
      if (await Dedupe.seen(fingerprint)) { setBusy(submit, false); showStatus(app, 'error', `<p>${esc(i18n.duplicate)}</p>`); return; }
      submit.textContent = i18n.sending;
      const result = await Zoho.submit(zoho, { first: name.first, last: name.last, email: value(app, 'email'), phone: value(app, 'phone'), subject, description, classification: cfg.classification, category: cfg.category }, attach);
      setBusy(submit, false);
      // Zoho answered (it redirects back to us, but Shopify won't let that page show inside a frame, so we can't
      // always read it). Any answer means the form was delivered; only silence (timeout) is treated as a problem.
      if (result === 'ok' || result === 'unconfirmed') {
        await Dedupe.mark(fingerprint);
        const doneStep = $('[data-final]', app);
        $('[data-done-email]', doneStep).textContent = value(app, 'email');
        flow.go(flow.steps.indexOf(doneStep));
      } else {
        showStatus(app, 'warn', `<p class="cf-status__title">${esc(i18n.unconfirmed_title)}</p><p>${esc(fmt(i18n.unconfirmed_text, { email: value(app, 'email') }))}</p>`);
      }
    });
  }

  /* ================================================================== SPONSOR MY RIDEOUT */
  function initRideout(root) {
    const i18n = readI18n(root);
    const cfg = readJson(root, '[data-config]');
    const zoho = Zoho.parse($('template[data-zoho]', root));
    const app = $('[data-app]', root);
    if (!zoho) { $('[data-unavailable]', root).hidden = false; app.hidden = true; return; }
    const flow = new Flow(app, i18n);

    const adult = $('[data-adult]', app);
    $$('input[name="over18"]', app).forEach((r) => r.addEventListener('change', () => {
      if (!r.checked) return;
      const minor = r.value === 'no';
      adult.hidden = !minor; $$('input, select', adult).forEach((f) => { f.disabled = !minor; });
    }));
    const date = $('#RideDate', app);
    date.min = todayIso();
    const shortNote = $('[data-short-notice]', app);
    date.addEventListener('change', () => {
      const days = Math.round((Date.parse(`${date.value}T00:00:00Z`) - Date.parse(`${todayIso()}T00:00:00Z`)) / 86400000);
      shortNote.hidden = !(days >= 0 && days < cfg.shortNoticeDays);
    });
    const files = new FilePicker($('[data-files="artwork"]', app), { max: zoho.files.length, accept: /^(image\/(jpeg|png|webp|heic|heif|gif|svg\+xml)|application\/pdf)$/, acceptExt: /\.(heic|heif)$/i, maxBytes: cfg.zohoMaxBytes, compress: false, i18n });

    flow.onEnter = (step) => {
      if (step.dataset.stepName !== 'review') return;
      const rows = [
        [i18n.r_event, value(app, 'event_name')], [i18n.r_where, `${value(app, 'venue')}, ${value(app, 'city')}, ${value(app, 'country')}`],
        [i18n.r_when, `${longDate(value(app, 'event_date'))}${value(app, 'start_time') ? ` · ${value(app, 'start_time')}` : ''}`],
        [i18n.r_riders, value(app, 'expected_riders')], [i18n.r_asks, value(app, 'support').join(', ')],
        [i18n.r_files, files.files.map((f) => f.name).join(', ')], [i18n.r_contact, `${value(app, 'name')} · ${value(app, 'email')}`],
      ];
      $('[data-review]', app).innerHTML = rows.map(([k, v]) => `<div class="cf-review__row"><dt>${esc(k)}</dt><dd>${esc(v || '—')}</dd></div>`).join('');
    };
    flow.onValidate = (step) => {
      if (step.dataset.stepName === 'ask' && !value(app, 'support').length) { const first = $('input[name="support"]', app); flow.flag(first, i18n.required_choice); return first; }
      return null;
    };

    const submit = $('[data-submit]', app);
    submit.addEventListener('click', async (e) => {
      e.preventDefault();
      if (!flow.validate()) return;
      if (value(app, 'website_url')) return;
      const city = value(app, 'city');
      const eventDate = value(app, 'event_date');
      const days = Math.round((Date.parse(`${eventDate}T00:00:00Z`) - Date.parse(`${todayIso()}T00:00:00Z`)) / 86400000);
      const riders = Number(value(app, 'expected_riders')) || 0;
      const minor = value(app, 'over18') === 'no';
      const flags = [
        days < cfg.shortNoticeDays ? `SHORT NOTICE (${days} days)` : '',
        minor ? 'ORGANISER UNDER 18 - adult contact supplied' : '',
        riders >= cfg.largeEventRiders ? `LARGE EVENT (${riders} expected)` : '',
        value(app, 'permission') !== 'yes' ? `PERMISSION: ${value(app, 'permission')}` : '',
      ].filter(Boolean);
      const name = splitName(value(app, 'name'));
      // Subject follows the agreed pattern: Rideout Sponsorship Request | City | 15 November 2026 (English date for routing)
      const subject = [cfg.subjectPrefix, city, longDate(eventDate, 'en-GB')].join(' | ');
      const description = [
        cfg.subjectPrefix.toUpperCase(),
        flags.length ? `FLAGS: ${flags.join(' / ')}` : 'FLAGS: none',
        '',
        lines([
          ['Event', value(app, 'event_name')], ['Type', value(app, 'event_type')], ['Date', eventDate], ['Start time', value(app, 'start_time')],
          ['Venue / meeting point', value(app, 'venue')], ['City', city], ['Country', value(app, 'country')],
          ['Expected riders', value(app, 'expected_riders')], ['Permission from venue / local authority', value(app, 'permission')],
          ['Support requested', value(app, 'support')],
          ['Organiser', value(app, 'name')], ['Organiser email', value(app, 'email')], ['Organiser phone', value(app, 'phone')],
          ['Instagram', value(app, 'instagram')], ['Crew / club', value(app, 'crew')], ['Organiser 18 or over', minor ? 'no' : 'yes'],
          ['Adult contact', minor ? `${value(app, 'adult_name')} (${value(app, 'adult_relationship')}) · ${value(app, 'adult_email')} · ${value(app, 'adult_phone')}` : ''],
          ['Previous events', value(app, 'previous')],
          ['Files attached', String(files.files.length)],
        ]),
        '',
        `ABOUT THE EVENT\n${value(app, 'about')}`,
        '',
        `SAFETY PLAN\n${value(app, 'safety')}`,
      ].join('\n');
      if (zoho.descMax && description.length > zoho.descMax) { showStatus(app, 'error', `<p>${esc(fmt(i18n.too_long, { over: description.length - zoho.descMax }))}</p>`); return; }
      const fingerprint = `${value(app, 'email')}|${subject}`;
      if (await Dedupe.seen(fingerprint)) { showStatus(app, 'error', `<p>${esc(i18n.duplicate)}</p>`); return; }
      setBusy(submit, true, i18n.sending);
      // Under-18 organisers: the adult is the ticket contact so replies go to them, the organiser is copied in the details.
      const contact = minor ? { ...splitName(value(app, 'adult_name')), email: value(app, 'adult_email'), phone: value(app, 'adult_phone') } : { ...name, email: value(app, 'email'), phone: value(app, 'phone') };
      const result = await Zoho.submit(zoho, { ...contact, subject, description, classification: cfg.classification, category: cfg.category }, files.files);
      setBusy(submit, false);
      if (result === 'ok' || result === 'unconfirmed') {
        await Dedupe.mark(fingerprint);
        const doneStep = $('[data-final]', app);
        $('[data-done-email]', doneStep).textContent = contact.email;
        flow.go(flow.steps.indexOf(doneStep));
      } else {
        showStatus(app, 'warn', `<p class="cf-status__title">${esc(i18n.unconfirmed_title)}</p><p>${esc(fmt(i18n.unconfirmed_text, { email: contact.email }))}</p>`);
      }
    });
  }

  /* ------------------------------------------------------------------ boot */
  const boot = () => {
    // "Today" comes from the visitor's device, not the shop's time zone, so nobody is told today's date is in the future.
    $$('input[type=date][data-max-today]').forEach((el) => { el.max = todayIso(); if (!el.min) el.min = '2000-01-01'; }); // also stops slips like year 0026
    $$('[data-cf="register"]').forEach(async (el) => {
      const complete = new URLSearchParams(location.search).get('complete');
      if (complete && /^\d+$/.test(complete) && await initComplete(el, complete)) return;
      initRegister(el);
    });
    $$('[data-cf="my-bikes"]').forEach(initMyBikes);
    $$('[data-cf="claim"]').forEach(initClaim);
    $$('[data-cf="rideout"]').forEach(initRideout);
  };
  window.CollectiveForms = { Zoho, Service, compressImage };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
